import type { ReactNode } from 'react'

import estilos from './Tabla.module.css'

export type ColumnaTabla<T> = {
  /** Encabezado. Vacío convierte la celda en el título de la ficha en celular. */
  encabezado: string
  /** Cómo se dibuja la celda. Recibe la fila completa. */
  celda: (fila: T) => ReactNode
  /** Cifras y fechas van a la derecha. */
  derecha?: boolean
  /** Ancho fijo, para columnas de estado o de acciones. */
  ancho?: string
}

type PropsTabla<T> = {
  columnas: ColumnaTabla<T>[]
  filas: T[]
  /** Identificador estable de cada fila. Nunca el índice. */
  claveFila: (fila: T) => string
  /** Si se pasa, las filas se pueden abrir. */
  onAbrirFila?: (fila: T) => void
  /** Qué mostrar cuando no hay nada. */
  vacio?: ReactNode
}

/**
 * Tabla del sistema.
 *
 * En pantalla ancha es una tabla; en celular cada fila se vuelve una ficha con
 * sus datos etiquetados, sin desplazamiento horizontal. Es el mismo componente:
 * mantener dos vistas separadas garantiza que una de las dos se quede atrás.
 */
export function Tabla<T>({ columnas, filas, claveFila, onAbrirFila, vacio }: PropsTabla<T>) {
  if (filas.length === 0 && vacio) {
    return <>{vacio}</>
  }

  return (
    <div className={estilos.contenedor}>
      <table className={`${estilos.tabla} ${onAbrirFila ? estilos.interactiva : ''}`}>
        <thead>
          <tr>
            {columnas.map((columna) => (
              <th
                key={columna.encabezado}
                style={columna.ancho ? { width: columna.ancho } : undefined}
                className={columna.derecha ? estilos.derecha : undefined}
              >
                {columna.encabezado}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filas.map((fila) => (
            <tr
              key={claveFila(fila)}
              onClick={onAbrirFila ? () => onAbrirFila(fila) : undefined}
              // La fila abrible también se alcanza con el teclado: en la
              // oficina se navega con tabulador, no siempre con el ratón.
              tabIndex={onAbrirFila ? 0 : undefined}
              role={onAbrirFila ? 'button' : undefined}
              onKeyDown={
                onAbrirFila
                  ? (evento) => {
                      if (evento.key === 'Enter' || evento.key === ' ') {
                        evento.preventDefault()
                        onAbrirFila(fila)
                      }
                    }
                  : undefined
              }
            >
              {columnas.map((columna) => (
                <td
                  key={columna.encabezado}
                  data-etiqueta={columna.encabezado}
                  className={columna.derecha ? estilos.derecha : undefined}
                >
                  {columna.celda(fila)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Celda de dos líneas: el dato principal y su aclaración. */
export function CeldaDoble({ principal, secundario }: { principal: ReactNode; secundario?: ReactNode }) {
  return (
    <div>
      <div className={estilos.principal}>{principal}</div>
      {secundario && <div className={estilos.secundario}>{secundario}</div>}
    </div>
  )
}
